import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import styles from './image-to-text.component.module.scss';
import { FullscreenHost } from '../fullscreen-host.component';
import { genStyles } from '../converter-layout/generator-layout';

interface OcrState {
    imageUrl: string | null;
    text: string;
    progress: number;
    stage: string;
    confidence: number;
    error: string;
    copiedField: string | null;
}

export default class ImageToTextComponent extends Component<{}, OcrState> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    private worker: any = null;

    state: OcrState = {
        imageUrl: null, text: '', progress: 0, stage: '', confidence: 0,
        error: '', copiedField: null,
    };

    handleFile = async (file: File) => {
        if (!file.type.startsWith('image/')) {
            this.setState({ error: 'Unsupported file type. Please upload a JPG, PNG, GIF, BMP, or WebP image.' });
            return;
        }
        const url = URL.createObjectURL(file);
        this.setState({ imageUrl: url, text: '', error: '', stage: 'Loading OCR engine...', progress: 0 });

        try {
            const Tesseract = await import('tesseract.js');
            this.worker = await Tesseract.createWorker('eng', 1, {
                logger: (m: any) => {
                    if (m.status === 'recognizing text') {
                        this.setState({ progress: Math.round(m.progress * 100), stage: 'Recognizing text...' });
                    }
                },
            });
            this.setState({ stage: 'Recognizing text...', progress: 10 });
            const { data } = await this.worker.recognize(url);
            this.setState({
                text: data.text.trim(),
                confidence: Math.round(data.confidence),
                stage: 'Done',
                progress: 100,
            });
            if (!data.text.trim()) {
                this.setState({ stage: 'No text detected in this image. Tips: ensure text is clear, well-lit, and at least 12px tall.' });
            }
        } catch (e: any) {
            this.setState({ error: 'Could not load the OCR engine. Please check your internet connection and try again.', stage: '' });
        }
    };

    handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file) this.handleFile(file);
    };

    handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) this.handleFile(file);
    };

    handlePaste = (e: React.ClipboardEvent) => {
        const items = e.clipboardData.items;
        for (const item of Array.from(items)) {
            if (item.type.startsWith('image/')) {
                const file = item.getAsFile();
                if (file) this.handleFile(file);
                break;
            }
        }
    };

    copyText = () => {
        navigator.clipboard.writeText(this.state.text).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copiedField: 'text' });
            this.copyTimeout = setTimeout(() => this.setState({ copiedField: null }), 2000);
        });
    };

    render() {
        const { imageUrl, text, progress, stage, confidence, error, copiedField } = this.state;
        const isCopied = copiedField === 'text';
        const isProcessing = stage && stage !== 'Done' && !stage.startsWith('No text');

        return (
            <FullscreenHost>
            <div className="row" onPaste={this.handlePaste}>
                {!imageUrl ? (
                    <div className="col-12">
                        <div className={genStyles.section}>
                            <div className={styles.dropZone} onDrop={this.handleDrop}
                                onDragOver={e => e.preventDefault()}>
                                <input type="file" accept="image/*" onChange={this.handleInputChange}
                                    className={styles.fileInput} id="ocr-file" />
                                <label htmlFor="ocr-file" className={styles.dropLabel}>
                                    <span className={styles.dropIcon}>📷</span>
                                    <span>Drop an image here, click to upload, or paste from clipboard</span>
                                </label>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="col-12 col-md-6">
                            <div className={genStyles.section}>
                                <img src={imageUrl} alt="Uploaded image" className={styles.preview} />
                            </div>
                        </div>
                        <div className="col-12 col-md-6">
                            <div className={genStyles.section}>
                                <textarea className={sharedStyles.textarea} value={text} readOnly rows={10}
                                    placeholder="Extracted text will appear here..." />
                                {text && (
                                    <div className={styles.footer}>
                                        <button className={`${sharedStyles.copyButton} ${isCopied ? sharedStyles.copyButtonSuccess : ''}`}
                                            onClick={this.copyText}>
                                            {isCopied ? '✓ Copied' : 'Copy'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}

                <div className="col-12">
                    <div className={genStyles.section}>
                        {isProcessing && (
                            <div className={styles.progressBar}>
                                <div className={styles.progressFill} style={{ width: `${progress}%` }} />
                                <span className={styles.progressText}>{progress}% — {stage}</span>
                            </div>
                        )}
                        {stage === 'Done' && confidence > 0 && (
                            <div className={styles.meta}>
                                <span>Confidence: <strong>{confidence}%</strong></span>
                                {confidence < 50 && (
                                    <span className={sharedStyles.warningMessage}>
                                        ⚠ Low confidence — results may be inaccurate. Try a clearer image.
                                    </span>
                                )}
                            </div>
                        )}
                        {stage && !isProcessing && stage !== 'Done' && (
                            <div className={styles.noText}>{stage}</div>
                        )}
                        {error && <div className={sharedStyles.errorMessage} aria-live="polite">⚠ {error}</div>}
                        {imageUrl && (
                            <button className={sharedStyles.copyButton} style={{ marginTop: '0.5rem' }}
                                onClick={() => this.setState({ imageUrl: null, text: '', stage: '', error: '', confidence: 0 })}>
                                Upload another image
                            </button>
                        )}
                    </div>
                </div>
            </div>
            </FullscreenHost>
        );
    }
}
